import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Wand2, GraduationCap, RefreshCw } from "lucide-react";

export default function SavePlanTemplateDialog({ open, onOpenChange, projectId, defaultName, defaultLearned = false, onSaved }) {
  const { user } = useAuth();
  const [name, setName] = useState(defaultName || "");
  const [learned, setLearned] = useState(defaultLearned);
  const [target, setTarget] = useState("new");
  const [myLearned, setMyLearned] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaultName || "");
      setLearned(defaultLearned);
      setTarget("new");
      api.get("/plan-templates")
        .then(({ data }) => setMyLearned(data.filter((t) => t.learned && user && (t.owner_id === user.id || user.role === "admin"))))
        .catch(() => setMyLearned([]));
    }
  }, [open, defaultName, defaultLearned, user]);

  const relearning = learned && target !== "new";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name, learned };
      if (relearning) payload.relearn_template_id = target;
      const { data } = await api.post(`/projects/${projectId}/save-plan-template`, payload);
      toast.success(data.relearned
        ? `"${data.name}" re-learned from this project's results (${data.task_count} tasks) — estimates updated`
        : `${learned ? "Learned template" : "Template"} "${data.name}" saved (${data.task_count} tasks) — find it in the Plan wizard`, { duration: 6000 });
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the template");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="save-plan-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Wand2 className="h-5 w-5 text-primary" /> Save as wizard template</DialogTitle>
          <DialogDescription>Reuse this plan in the Plan Wizard — plug in a start date or a target end date and get a full plan instantly.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <label className="flex items-start gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 cursor-pointer" data-testid="plan-template-learned-toggle">
            <Checkbox checked={learned} onCheckedChange={(v) => setLearned(!!v)} className="mt-0.5" data-testid="plan-template-learned-checkbox" />
            <span className="text-sm">
              <span className="font-bold flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-emerald-600" /> Learned template</span>
              <span className="text-xs text-muted-foreground">Capture the real results: actual durations, baseline vs. actual variance per stage, and this team's velocity — so future plans set honest time expectations.</span>
            </span>
          </label>
          {learned && myLearned.length > 0 && (
            <div>
              <Label>Save as</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="mt-1.5" data-testid="plan-template-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new" data-testid="plan-template-target-new">New template</SelectItem>
                  {myLearned.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`plan-template-target-${t.id}`}>
                      Re-learn "{t.name}" ({t.relearn_count ? `refined ${t.relearn_count}×` : "1 project so far"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {relearning && <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><RefreshCw className="h-3 w-3" /> The template's tasks, durations and insights will be updated from this project's actual results.</p>}
            </div>
          )}
          {!relearning && (
            <div>
              <Label htmlFor="ptpl-name">Template name</Label>
              <Input id="ptpl-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Product Launch" className="mt-1.5" data-testid="plan-template-name-input" />
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving || (!relearning && !name.trim())} className="gap-2" data-testid="plan-template-save-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : relearning ? <RefreshCw className="h-4 w-4" /> : <Wand2 className="h-4 w-4" />}
              {relearning ? "Re-learn template" : "Save template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
